import QRCode from 'qrcode'

/**
 * Character set options per EPC069-12
 */
export const CHARACTER_SETS = {
  1: { code: '1', name: 'UTF-8', encoding: 'UTF-8' },
  2: { code: '2', name: 'ISO 8859-1', encoding: 'ISO-8859-1' },
  3: { code: '3', name: 'ISO 8859-2', encoding: 'ISO-8859-2' },
  4: { code: '4', name: 'ISO 8859-4', encoding: 'ISO-8859-4' },
  5: { code: '5', name: 'ISO 8859-5', encoding: 'ISO-8859-5' },
  6: { code: '6', name: 'ISO 8859-7', encoding: 'ISO-8859-7' },
  7: { code: '7', name: 'ISO 8859-10', encoding: 'ISO-8859-10' },
  8: { code: '8', name: 'ISO 8859-15', encoding: 'ISO-8859-15' },
} as const

export type CharacterSetCode = keyof typeof CHARACTER_SETS

/**
 * SEPA QR code data elements per EPC069-12 v3.1
 */
export interface SepaQrData {
  /** BIC of beneficiary PSP (optional for V2 in EEA) */
  bic?: string
  /** Name of beneficiary (max 70 chars) */
  name: string
  /** IBAN of beneficiary (max 34 chars) */
  iban: string
  /** Amount in EUR (0.01 - 999999999.99) */
  amount?: number
  /** Purpose code (max 4 chars) */
  purpose?: string
  /** Structured remittance reference (max 35 chars, ISO 11649) */
  reference?: string
  /** Unstructured remittance text (max 140 chars) */
  text?: string
  /** Beneficiary to originator information (max 70 chars) */
  information?: string
  /** Character set (1-8, default 1 = UTF-8) */
  charset?: CharacterSetCode
  /** Use instant SCT identification */
  instant?: boolean
}

/**
 * QR generation options
 */
export interface QrOptions {
  /** Size in pixels (default 256) */
  size?: number
  /** Error correction level (default M = 15%) */
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H'
  /** Margin (default 4) */
  margin?: number
}

/**
 * Validation result for SEPA QR data
 */
export interface ValidationResult {
  valid: boolean
  errors: string[]
  warnings: string[]
  payloadSize: number
}

/**
 * Maximum payload size per EPC069-12 (331 bytes)
 */
const MAX_PAYLOAD_SIZE = 331

/**
 * Validate amount format and range
 */
function validateAmount(amount: number): string | null {
  if (amount < 0.01) return 'Amount must be at least 0.01 EUR'
  if (amount > 999999999.99) return 'Amount cannot exceed 999,999,999.99 EUR'
  if (!Number.isFinite(amount)) return 'Invalid amount'
  return null
}

/**
 * Format amount for QR payload (EUR prefix, max 2 decimals)
 */
function formatAmount(amount: number): string {
  return `EUR${amount.toFixed(2).replace(/\.?0+$/, '')}`
}

/**
 * Validate SEPA QR data and calculate payload size
 */
export function validateSepaQrData(data: SepaQrData): ValidationResult {
  const errors: string[] = []
  const warnings: string[] = []

  // Required fields
  if (!data.name || data.name.trim().length === 0) {
    errors.push('Beneficiary name is required')
  } else if (data.name.length > 70) {
    errors.push('Beneficiary name exceeds 70 characters')
  }

  if (!data.iban || data.iban.trim().length === 0) {
    errors.push('IBAN is required')
  } else if (data.iban.replace(/\s/g, '').length > 34) {
    errors.push('IBAN exceeds 34 characters')
  }

  // Amount is required for RTP (even though EPC standard allows optional)
  if (data.amount === undefined || data.amount === null) {
    errors.push('Amount is required')
  } else {
    const amountError = validateAmount(data.amount)
    if (amountError) errors.push(amountError)
  }

  // Optional field length validations
  if (data.bic && data.bic.length > 11) {
    errors.push('BIC exceeds 11 characters')
  }

  if (data.purpose && data.purpose.length > 4) {
    errors.push('Purpose code exceeds 4 characters')
  }

  if (data.reference && data.text) {
    errors.push('Cannot specify both reference and text (mutually exclusive)')
  }

  if (data.reference && data.reference.length > 35) {
    errors.push('Remittance reference exceeds 35 characters')
  }

  if (data.text && data.text.length > 140) {
    errors.push('Remittance text exceeds 140 characters')
  }

  if (data.information && data.information.length > 70) {
    errors.push('Information field exceeds 70 characters')
  }

  // Build payload to check size
  const payload = buildPayload(data)
  const payloadSize = new TextEncoder().encode(payload).length

  if (payloadSize > MAX_PAYLOAD_SIZE) {
    errors.push(`Payload size (${payloadSize} bytes) exceeds maximum (${MAX_PAYLOAD_SIZE} bytes)`)
  } else if (payloadSize > MAX_PAYLOAD_SIZE * 0.9) {
    warnings.push(`Payload size (${payloadSize} bytes) is close to maximum (${MAX_PAYLOAD_SIZE} bytes)`)
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    payloadSize
  }
}

/**
 * Build EPC069-12 V2 payload string
 */
export function buildPayload(data: SepaQrData): string {
  const charset = data.charset || 1
  const identification = 'SCT' // Same for regular and instant per EPC guidelines

  const lines = [
    'BCD',                                      // Service tag
    '002',                                      // Version (V2)
    charset.toString(),                         // Character set
    identification,                             // Identification (SCT)
    data.bic || '',                            // BIC (optional for V2)
    data.name,                                 // Beneficiary name
    data.iban.replace(/\s/g, '').toUpperCase(), // IBAN (cleaned)
    data.amount ? formatAmount(data.amount) : '', // Amount
    data.purpose || '',                        // Purpose code
    data.reference || '',                      // Remittance reference
    data.text || '',                           // Remittance text
    data.information || ''                     // Beneficiary info
  ]

  // Remove trailing empty lines
  while (lines.length > 0 && lines[lines.length - 1] === '') {
    lines.pop()
  }

  return lines.join('\n')
}

/**
 * Parse EPC069-12 payload string back to data object
 */
export function parsePayload(payload: string): SepaQrData | null {
  const lines = payload.split('\n')

  if (lines.length < 7) return null
  if (lines[0] !== 'BCD') return null
  if (lines[1] !== '001' && lines[1] !== '002') return null
  if (lines[3] !== 'SCT') return null

  const amountStr = lines[7] || ''
  let amount: number | undefined
  if (amountStr.startsWith('EUR')) {
    amount = parseFloat(amountStr.slice(3))
    if (isNaN(amount)) amount = undefined
  }

  return {
    charset: parseInt(lines[2], 10) as CharacterSetCode,
    bic: lines[4] || undefined,
    name: lines[5],
    iban: lines[6],
    amount,
    purpose: lines[8] || undefined,
    reference: lines[9] || undefined,
    text: lines[10] || undefined,
    information: lines[11] || undefined
  }
}

/**
 * Generate QR code as data URL (PNG base64)
 */
export async function generateQrDataUrl(
  data: SepaQrData,
  options: QrOptions = {}
): Promise<string> {
  const payload = buildPayload(data)
  const {
    size = 256,
    errorCorrectionLevel = 'M',
    margin = 4
  } = options

  return QRCode.toDataURL(payload, {
    errorCorrectionLevel,
    margin,
    width: size,
    type: 'image/png'
  })
}

/**
 * Generate QR code as canvas element
 */
export async function generateQrCanvas(
  data: SepaQrData,
  canvas: HTMLCanvasElement,
  options: QrOptions = {}
): Promise<void> {
  const payload = buildPayload(data)
  const {
    size = 256,
    errorCorrectionLevel = 'M',
    margin = 4
  } = options

  await QRCode.toCanvas(canvas, payload, {
    errorCorrectionLevel,
    margin,
    width: size
  })
}

/**
 * Generate QR code as SVG string
 */
export async function generateQrSvg(
  data: SepaQrData,
  options: QrOptions = {}
): Promise<string> {
  const payload = buildPayload(data)
  const {
    errorCorrectionLevel = 'M',
    margin = 4
  } = options

  return QRCode.toString(payload, {
    type: 'svg',
    errorCorrectionLevel,
    margin
  })
}

